/* eslint-disable @typescript-eslint/dot-notation */
/* eslint-disable @typescript-eslint/naming-convention */
import { Component, ElementRef, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import Rollbar from 'rollbar';
import { RollbarService } from '../rollbar_config';
import { InactivityTimeoutService } from './core/inactivity-timeout/inactivity-timeout.service';
import { SecurityService } from './core/security/security.service';
import { UserJsVm } from './shared/generated/Administration/Models/User/JavaScript/UserJsVm';
import { pcgSettings } from './shared/generated/pcg-settings';

/**
 * This is the application's root component.
 */
@Component({
    selector: 'pcg-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent {
	errorCache: any = {};
	// The following are error codes thrown as a result of system security
	// and should not be logged to rollbar.
	rollbarIgnoredCodes: number[] = [
		0, 		// Network Error / Aborted Request
		401, 	// Login redirect
		403, 	// Access denied
		467,	// 2fa not enabled
		503, 	// Service Unavailable
		504 	// Gateway timeout
	];

	// Inactivity timeout needs to be injected here to have a constant timeout going in the background
	constructor(
		inactiveTimeout: InactivityTimeoutService,
		router: Router,
		private sec: SecurityService,
		private modalConfig: NgbModalConfig,
		private elRef: ElementRef,
		@Inject(RollbarService) private rollbar: Rollbar
	) {
		this.configureRollbar(); // Set global rollbar settings
		this.configureModal(); // Set default modal settings
		this.setIsLoggedInHtmlClass(); // Handle is-logged-in HTML class

		// Dirty AngularJS style global router used in index.html
		window['$router'] = router;
	}

	/** Set default ng-bootstrap modal options */
	configureModal() {
		// Disable ng bootstrap modal animations by default
		this.modalConfig.animation = false;
		this.modalConfig.ariaLabelledBy = 'modal-basic-title';
	}

	/** Configure rollbar throughout the website */
	configureRollbar() {
		this.rollbar.configure({
			// Set initial user on load (updated in subscription below)
			payload: this.getRollbarPayload(this.sec.getUser()),
			checkIgnore: (uncaught, args, item) => {
				// Prevent duplicate message/URL combinations for the session
				// Don't want to flood rollbar with errors if Angular starts looping
				// an error message.
				const theItem = item as any;
				const errorKey =
					(theItem?.body?.message?.body ?? '') +
					(theItem?.body?.message?.extra?.url?.split('?')[0] ?? '') +
					window.location.href;
					
				// Do not report these errors as they are results of intented system behavior.
				if (this.errorCache[errorKey] || (theItem?.body?.message?.extra?.status && this.rollbarIgnoredCodes.some(theItem?.body?.message?.extra?.status))) { 
					return true; 
				}

				this.errorCache[errorKey] = true;

				// Prevent errors in local development
				if (pcgSettings.mode === 'Development') { 
					return true;
				 }

				return false;
			},
		});

		// Update user if it changes
		this.sec.user$.subscribe(newUser => {
			this.rollbar.configure({
				payload: this.getRollbarPayload(newUser),
			});
		});
	}

	/**
	 * Creates a rollbar payload with user added
	 * 
	 * @param user The user to add
	 */
	getRollbarPayload(user: UserJsVm): Rollbar.Payload {
		return {
			environment: pcgSettings.mode,
			person: !user
				? undefined
				: {
						id: user.id.toString(),
						username: user.userName,
						email: user.email,
				  },
			client: {
				javascript: {
					source_map_enabled: true,
					code_version: 'c22c1ac',
					guess_uncaught_frames: true
				}
			}			
		};
	}

	/** Sets the is-logged-in HTML class, which is used to hide the release notes links */
	setIsLoggedInHtmlClass() {
		// Add / remove is-logged-in class
		this.sec.user$.subscribe(user => {
			const isLoggedIn = user ? true : false;
			if (isLoggedIn) {
				this.elRef.nativeElement.classList.add('is-logged-in');
			} else {
				this.elRef.nativeElement.classList.remove('is-logged-in');
			}
		});
	}
}
